<template>
  <div :class="computedClasses">
    <SbIcon class="sb-notification__icon-container" :name="iconStatus" />
    <div class="sb-notification__wrapper">
      <span
        v-if="title"
        class="sb-notification__title"
        data-testid="sb-notification-title"
      >
        {{ title }}
      </span>
      <div
        v-if="isDescriptionVisible"
        class="sb-notification__description"
        data-testid="sb-notification-description"
      >
        {{ description }}
        <slot></slot>
      </div>
      <button
        v-if="isExpandable"
        class="sb-notification__btn"
        :aria-expanded="expanded"
        :aria-label="expanded ? 'Collapse notification' : 'Expand notification'"
        @click.prevent.stop="toggleExpandedNotification"
      >
        <SbIcon :name="chevronIcon" />
      </button>
      <div v-if="isLinkVisible" class="sb-notification__link">
        <a :href="link" target="_blank" :title="`Link to ${linkName}`">
          {{ linkName }}
          <SbIcon v-if="isFull && !isExpandable" name="chevron-right" />
        </a>
      </div>
    </div>
    <button
      v-if="isCloseable && !isExpandable"
      class="sb-notification__btn sb-notification__btn--close"
      aria-label="Close notification"
      @click="handleClose"
    >
      <SbIcon name="x" />
    </button>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, useSlots } from 'vue'
import SbIcon from '../Icon'
import type { NotificationStatus } from './types'

const notificationIcons = {
  error: 'octagon-alert',
  info: 'info',
  general: 'info',
  warning: 'triangle-alert',
  positive: 'circle-check',
  negative: 'octagon-alert',
} as const

export type SbNotificationProps = {
  status?: NotificationStatus
  title?: string
  description?: string
  link?: string
  linkName?: string
  isExpandable?: boolean
  isFull?: boolean
  isCloseable?: boolean
}

const props = withDefaults(defineProps<SbNotificationProps>(), {
  status: 'general',
  title: 'notification',
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const expanded = ref(false)

const slots = useSlots()

const computedClasses = computed(() => {
  const hasContent = props.description || props.link || slots.default
  const hasFullContent = props.isFull && !props.isExpandable && hasContent
  const isRegularContent = !props.isFull && !props.isExpandable && hasContent
  return [
    `sb-notification sb-notification--${props.status}`,
    {
      'sb-notification--expandable': props.isExpandable,
      'sb-notification--full': props.isFull,
      'sb-notification--content': isRegularContent,
      'sb-notification--full-content': hasFullContent,
    },
  ]
})

const isExpandableAndExpanded = computed(
  () => props.isExpandable && expanded.value,
)

const chevronIcon = computed(() =>
  expanded.value ? 'chevron-up' : 'chevron-down',
)

const isDescriptionVisible = computed(
  () =>
    (props.description || slots.default) &&
    (isExpandableAndExpanded.value || !props.isExpandable),
)

const isLinkVisible = computed(
  () =>
    props.link &&
    props.linkName &&
    (isExpandableAndExpanded.value || !props.isExpandable),
)

const iconStatus = computed(() => notificationIcons[props.status])

function toggleExpandedNotification() {
  expanded.value = !expanded.value
}

function handleClose() {
  emit('close')
}
</script>
